import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, RefreshCw } from "lucide-react";

const STATUSES = ["new", "contacted", "shortlisted", "closed"];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export default function AdminInbox({ token }) {
  const [kind, setKind] = useState("officials");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/inbox/${kind}`, {
        headers: { "X-Admin-Token": token },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to load");
      setItems(data.applications || data.enquiries || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [kind, token]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/inbox/${kind}/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Admin-Token": token },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
      toast.success(`Marked ${status}`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div data-testid="admin-inbox">
      <div className="flex flex-wrap items-center gap-3">
        {["officials", "partners"].map((k) => (
          <button
            key={k}
            onClick={() => setKind(k)}
            data-testid={`inbox-tab-${k}`}
            className={`font-mono text-[11px] tracking-[0.2em] px-5 py-2.5 border transition-colors ${
              kind === k ? "border-[#ff5a1f] text-[#ff5a1f]" : "border-[#2e2e2a] text-[#6e6e6a] hover:text-[#f0eee4]"
            }`}
          >
            {k === "officials" ? "OFFICIAL APPLICATIONS" : "PARTNER ENQUIRIES"}
          </button>
        ))}
        <button onClick={load} data-testid="inbox-refresh" className="ml-auto p-2.5 border border-[#2e2e2a] text-[#6e6e6a] hover:text-[#f0eee4] transition-colors" aria-label="Refresh">
          <RefreshCw size={14} />
        </button>
      </div>

      {loading ? (
        <p className="mt-10 font-mono text-xs tracking-[0.2em] text-[#6e6e6a]" data-testid="inbox-loading">LOADING…</p>
      ) : items.length === 0 ? (
        <p className="mt-10 font-mono text-xs tracking-[0.2em] text-[#6e6e6a]" data-testid="inbox-empty">
          NO {kind === "officials" ? "APPLICATIONS" : "ENQUIRIES"} YET
        </p>
      ) : (
        <div className="mt-6 space-y-3" data-testid="inbox-list">
          {items.map((it) => (
            <div key={it.id} className="border border-[#2e2e2a] p-4 sm:p-5" data-testid={`inbox-item-${it.id.slice(0, 8)}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-display font-bold text-xl uppercase truncate">
                    {kind === "officials" ? it.full_name : it.company}
                  </p>
                  <p className="mt-1 font-mono text-[10px] tracking-[0.15em] text-[#a3a39f]">
                    {kind === "officials" ? it.role : it.partnership_type}
                    {it.budget ? ` — ${it.budget}` : ""}
                  </p>
                  <p className="mt-2 font-mono text-[10px] tracking-[0.1em] text-[#6e6e6a] break-all">
                    {it.email} · {it.phone || "—"} · {kind === "officials" ? it.city : it.website || "—"} · {fmtDate(it.created_at)}
                  </p>
                  {(it.reason || it.proposal) && (
                    <p className="mt-3 text-sm text-[#a3a39f] leading-relaxed max-w-2xl">{it.reason || it.proposal}</p>
                  )}
                  {kind === "officials" && (
                    <p className="mt-2 font-mono text-[10px] tracking-[0.1em] text-[#6e6e6a]">
                      {it.certification}
                      {it.cert_name_org ? ` — ${it.cert_name_org}` : ""} · {it.availability}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {it.certificate_url && (
                    <a
                      href={it.certificate_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`inbox-cert-${it.id.slice(0, 8)}`}
                      className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.15em] border border-[#454540] px-3 py-2 text-[#a3a39f] hover:text-[#f0eee4] hover:border-[#f0eee4] transition-colors"
                    >
                      <FileDown size={12} /> CERTIFICATE
                    </a>
                  )}
                  <select
                    value={it.status || "new"}
                    onChange={(e) => setStatus(it.id, e.target.value)}
                    data-testid={`inbox-status-${it.id.slice(0, 8)}`}
                    className={`bg-[#161614] border font-mono text-[10px] tracking-[0.15em] px-3 py-2 outline-none uppercase ${
                      (it.status || "new") === "new" ? "border-[#ff5a1f] text-[#ff5a1f]" : "border-[#2e2e2a] text-[#a3a39f]"
                    }`}
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
